import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, PanelRightClose } from 'lucide-react';
import { SAUDI_MODULE_STRUCTURE } from '../config/saudiModuleStructure';
import { SAUDI_BRAND } from '../config/saudiBrandConfig';
import { QAWAEM_LOGO_FULL } from '../config/qawaemAssets';
import { useSaudiSidebarLayout } from '../context/SaudiSidebarLayoutContext';
import featureService from '../../../core/services/featureService';
import './saudi-qawaem.css';

function isHrefActive(pathname, href) {
  if (!href) return false;
  if (href.startsWith('/saudi/pending/')) {
    return pathname === href;
  }
  if (href === '/dashboard') {
    return pathname === '/dashboard' || pathname === '/';
  }
  return pathname === href || pathname.startsWith(`${href}/`);
}

function isModuleActive(pathname, module) {
  return module.sections.some((s) => isHrefActive(pathname, s.href));
}

export default function SaudiSidebar() {
  const location = useLocation();
  const { open, close } = useSaudiSidebarLayout();
  const [expanded, setExpanded] = useState({});
  const [licenseRevision, setLicenseRevision] = useState(0);

  useEffect(() => {
    const onLicenseSynced = () => {
      featureService.clearCache();
      setLicenseRevision((n) => n + 1);
    };
    window.addEventListener('qawaem-license-synced', onLicenseSynced);
    return () => window.removeEventListener('qawaem-license-synced', onLicenseSynced);
  }, []);

  const licensedModules = useMemo(
    () => SAUDI_MODULE_STRUCTURE.filter((module) => featureService.hasModuleAccess(module.key)),
    [licenseRevision],
  );

  const activeModuleId = useMemo(() => {
    const hit = licensedModules.find((m) => isModuleActive(location.pathname, m));
    return hit?.id ?? null;
  }, [location.pathname, licensedModules]);

  useEffect(() => {
    if (activeModuleId) {
      setExpanded((prev) => ({ ...prev, [activeModuleId]: true }));
    }
  }, [activeModuleId]);

  const toggleModule = useCallback((moduleId) => {
    setExpanded((prev) => ({ ...prev, [moduleId]: !prev[moduleId] }));
  }, []);

  const closeOnMobile = () => {
    if (typeof window !== 'undefined' && window.matchMedia('(max-width: 1023px)').matches) {
      close();
    }
  };

  return (
    <nav
      aria-hidden={!open}
      className={`glass-sidebar h-screen shrink-0 sticky top-0 z-[9999] flex flex-col overflow-hidden transition-[width,opacity,transform] duration-300 ease-out ${
        open
          ? 'w-64 opacity-100 translate-x-0'
          : 'w-0 opacity-0 pointer-events-none translate-x-4 border-0 shadow-none'
      }`}
      dir="rtl"
    >
      <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 min-w-[16rem]">
        <div className="mb-6 flex items-start justify-between gap-2">
          <div className="flex flex-col items-center gap-2 text-center flex-1 min-w-0">
            <img src={QAWAEM_LOGO_FULL} alt={SAUDI_BRAND.appName} className="h-12 w-auto object-contain" />
            <p className="text-sm font-semibold text-forest-700">{SAUDI_BRAND.appSubtitle}</p>
          </div>
          <button
            type="button"
            onClick={close}
            className="shrink-0 p-2 rounded-xl text-forest-700 hover:bg-forest-100/80 border border-transparent hover:border-forest-200/80 transition-colors"
            title="إخفاء القائمة"
            aria-label="إخفاء القائمة"
          >
            <PanelRightClose className="h-5 w-5" />
          </button>
        </div>

        <ul className="space-y-1.5">
          {licensedModules.map((module) => {
            const ModuleIcon = module.icon;
            const isExpanded = expanded[module.id];
            const moduleActive = isModuleActive(location.pathname, module);

            return (
              <li key={module.id}>
                <button
                  type="button"
                  className={`qawaem-nav-link ${isExpanded || moduleActive ? 'is-active' : ''}`}
                  onClick={() => toggleModule(module.id)}
                  aria-expanded={isExpanded}
                >
                  <span className="flex items-center gap-2 min-w-0 flex-1">
                    <ModuleIcon className="h-5 w-5 shrink-0 text-forest-600" />
                    <span className="truncate">{module.label}</span>
                  </span>
                  <ChevronDown
                    className={`qawaem-nav-chevron h-4 w-4 shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                  />
                </button>

                {isExpanded ? (
                  <ul className="saudi-subsections space-y-0.5">
                    {module.sections.map((section) => {
                      const SectionIcon = section.icon;
                      const active = isHrefActive(location.pathname, section.href);
                      return (
                        <li key={section.id}>
                          <Link
                            to={section.href}
                            className={`qawaem-nav-sublink ${active ? 'is-active' : ''}`}
                            onClick={closeOnMobile}
                          >
                            {SectionIcon ? (
                              <SectionIcon className="h-5 w-5 shrink-0 text-forest-600" />
                            ) : null}
                            <span className="flex-1 truncate">{section.label}</span>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                ) : null}
              </li>
            );
          })}
        </ul>
      </div>

      <div className="shrink-0 border-t border-forest-200/50 bg-forest-50/20 p-4 min-w-[16rem]">
        <p className="text-[10px] font-bold uppercase tracking-wider text-console-subtle px-4 mb-2.5">
          الإعدادات
        </p>
        <ul className="space-y-1.5">
          <li>
            <Link
              to="/settings"
              className={`qawaem-nav-link ${location.pathname === '/settings' ? 'is-active' : ''}`}
              onClick={closeOnMobile}
            >
              <span className="truncate">إعدادات النظام</span>
            </Link>
          </li>
        </ul>
      </div>
    </nav>
  );
}
